import type React from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import styles from './OTPInput.module.scss';

interface OTPInputProps {
  length?: number;
  value?: string;
  onChange: (otp: string) => void;
  onComplete?: (otp: string) => void;
  autoFocus?: boolean;
  variant?: 'flat' | 'bordered' | 'underlined';
  allowedKeys?: string;
  anyAllowed?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({
  length = 4,
  value = '',
  onChange,
  onComplete,
  autoFocus = true,
  variant = 'underlined',
  allowedKeys = '^[0-9]*$',
  anyAllowed = false,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const keyRegex = useRef(new RegExp(allowedKeys));

  useEffect(() => {
    try {
      keyRegex.current = new RegExp(allowedKeys);
    } catch {
      keyRegex.current = new RegExp('^[0-9]*$');
    }
  }, [allowedKeys]);

  useEffect(() => {
    if (value) {
      const newOtp = value.split('').slice(0, length);
      const filledOtp = [...newOtp, ...Array(length - newOtp.length).fill('')];
      setOtp(filledOtp);
    } else {
      setOtp(Array(length).fill(''));
    }
  }, [value, length]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const isValidKey = (input: string): boolean => {
    if (anyAllowed) return true;
    return keyRegex.current.test(input);
  };

  const filterInput = (input: string): string => {
    if (anyAllowed) return input;

    return input
      .split('')
      .filter((char) => keyRegex.current.test(char))
      .join('');
  };

  const checkCompletion = (currentOtp: string[]) => {
    const otpString = currentOtp.join('');
    if (otpString.length === length && onComplete) {
      onComplete(otpString);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    let inputValue = e.target.value;

    if (!isValidKey(inputValue)) {
      return;
    }

    if (!anyAllowed) {
      inputValue = filterInput(inputValue);
    }

    if (inputValue.length > 1) {
      const pastedValues = inputValue.split('').slice(0, length - index);
      const newOtp = [...otp];

      pastedValues.forEach((val, i) => {
        if (index + i < length) {
          newOtp[index + i] = val;
        }
      });

      setOtp(newOtp);
      onChange(newOtp.join(''));
      checkCompletion(newOtp);

      const nextIndex = Math.min(index + pastedValues.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = inputValue;
    setOtp(newOtp);
    onChange(newOtp.join(''));
    checkCompletion(newOtp);

    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
        onChange(newOtp.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = useCallback((e) => {
    e.target.select();
  }, []);

  const handleRef = useCallback(
    (el: HTMLInputElement | null, index: number) => {
      if (el) {
        inputRefs.current[index] = el;
      }
    },
    [inputRefs],
  );

  const containerClass = clsx(styles.container, styles[`container--${variant}`]);

  const inputClass = (index: number) =>
    clsx(styles.input, styles[`input--${variant}`], {
      [styles.inputFilled]: otp[index],
      [styles.inputEmpty]: !otp[index],
    });

  return (
    <div className={containerClass}>
      {otp.map((value, index) => (
        <input
          key={index}
          type='text'
          inputMode={anyAllowed ? 'text' : 'numeric'}
          data-otp-cell=''
          maxLength={index === 0 ? length : 1}
          value={value}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onFocus={handleFocus}
          ref={(el) => handleRef(el, index)}
          className={inputClass(index)}
        />
      ))}
    </div>
  );
};

export { OTPInput };
